import { AuthRoutes, PrivateRoute } from '@codezo/auth';
import { AuthenticationUser, AuthRoleEnum } from '@codezo/graphql';
import { useCallback } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useParams } from 'react-router-dom';
import { AdminUserType, useAuth } from './hooks/useAuth';
import { PlatformLayout } from './layouts/PlatformLayout';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { BrandDetailPageV2 } from './pages/clients/v2/BrandDetailPageV2';
import { ClientDetailPageV2 } from './pages/clients/v2/ClientDetailPageV2';
import { ClientsPage } from './pages/clients/ClientsPage';
import { ContactsPage } from './pages/contacts/ContactsPage';
import { ManufacturerDetailPage } from './pages/manufacturers/ManufacturerDetailPage';
import { ManufacturersPage } from './pages/manufacturers/ManufacturersPage';
import { ProductDetailPage } from './pages/products/ProductDetailPage';
import { ProductsPage } from './pages/products/ProductsPage';
import { OrderQuoteDetailPageV2 } from './pages/quotes/v2/OrderQuoteDetailPageV2';
import { OrderDetailPageV2 } from './pages/orders/v2/OrderDetailPageV2';
import { OrdersPage } from './pages/orders/OrdersPage';
import { QuotesPage } from './pages/quotes/QuotesPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { UsersPage } from './pages/users/UsersPage';
import { VetterDetailPage } from './pages/vetters/VetterDetailPage';
import { VettersPage } from './pages/vetters/VettersPage';
import { NotFoundPage } from './pages/errors/NotFoundPage';
import { NotificationLinkPage } from './pages/notifications/NotificationLinkPage';
import { Toaster } from './components/ui/sonner';
import './styles.css';

export default function App() {
	const { isAuthenticated, user, setUser, setAccessToken, token } = useAuth();

	const formatLoginUser = useCallback((user: AuthenticationUser): AdminUserType => {
		return {
			id: user.id,
			email: user.email!,
			role: user.role,
			organizationId: user.organizationUser?.organizationId,
			organization: user.organizationUser?.organization
				? {
						id: user.organizationUser.organization.id,
						name: user.organizationUser.organization.name,
					}
				: undefined,
		};
	}, []);

	return (
		<>
			<Toaster position="bottom-right" />
			<Router>
				<AuthRoutes
					setUser={setUser}
					setAccessToken={setAccessToken}
					userFormatter={formatLoginUser}
					token={token}
					requiredRole={AuthRoleEnum.Admin}
				/>
				<Routes>
					{/* Protected routes */}
					<Route
						path="/"
						element={
							<PrivateRoute
								isAuthenticated={isAuthenticated}
								user={user}
								requiredRole={AuthRoleEnum.Admin}
							>
								<PlatformLayout />
							</PrivateRoute>
						}
					>
						<Route index element={<Navigate to="/dashboard" replace />} />
						<Route path="dashboard" element={<DashboardPage />} />
						<Route path="products" element={<ProductsPage />} />
						<Route path="products/:id" element={<ProductDetailPage />} />
						<Route path="quotes" element={<QuotesPage />} />
						<Route path="quotes/:id" element={<OrderQuoteDetailPageV2 />} />
						{/* The old preview URL. Links to it were handed out while v2 sat
						    alongside v1, so it keeps resolving rather than 404ing. */}
						<Route
							path="quotes/:id/v2"
							element={<Navigate to=".." relative="path" replace />}
						/>
						<Route path="orders" element={<OrdersPage />} />
						<Route path="orders/:id" element={<OrderDetailPageV2 />} />
						{/* The old page is gone; links handed out to it land on the current one. */}
						<Route
							path="orders/:id/v1"
							element={<Navigate to=".." relative="path" replace />}
						/>
						{/* The old page is gone; links handed out to it land on the current one. */}
						<Route
							path="orders/:id/old"
							element={<Navigate to=".." relative="path" replace />}
						/>
						{/* The old preview URL. Links to it were handed out while v2 sat
						    alongside v1, so it keeps resolving rather than 404ing — and
						    lands on v2, which is what it always pointed at. */}
						<Route
							path="orders/:id/v2"
							element={<Navigate to=".." relative="path" replace />}
						/>
						<Route path="suppliers" element={<ManufacturersPage />} />
						<Route path="suppliers/:id" element={<ManufacturerDetailPage />} />
						<Route path="vetters" element={<VettersPage />} />
						<Route path="vetters/:id" element={<VetterDetailPage />} />
						<Route path="clients" element={<ClientsPage />} />
						<Route path="clients/:id" element={<ClientDetailPageV2 />} />
						{/* The old page is gone; links handed out to it land on the current one. */}
						<Route
							path="clients/:id/v1"
							element={<Navigate to=".." relative="path" replace />}
						/>
						{/* The old preview URL. Links to it were handed out while v2 sat
						    alongside v1, so it keeps resolving rather than 404ing — and
						    lands on v2, which is what it always pointed at. */}
						<Route
							path="clients/:id/v2"
							element={<Navigate to=".." relative="path" replace />}
						/>
						{/* A brand kit belongs to one client, so it lives under it —
						    and having a route at all is what makes a brand reloadable
						    and linkable, which it was not as tab state. */}
						<Route path="clients/:id/brands/:brandId" element={<BrandDetailPageV2 />} />
						{/* Same old-preview-URL courtesy as above. The `v2` segment sits
						    in the MIDDLE here, so ".." cannot express the new path and
						    this rebuilds it from the params instead. */}
						<Route
							path="clients/:id/v2/brands/:brandId"
							element={<BrandKitRedirect />}
						/>
						<Route path="contacts" element={<ContactsPage />} />
						<Route path="users" element={<UsersPage />} />
						<Route path="settings" element={<SettingsPage />} />
						{/*
						 * Notification links. The bell carries a (type, id) pair and
						 * nothing about routing, so it points here and this resolves
						 * where the screen currently lives — the same contract the
						 * client app uses for emailed links.
						 */}
						<Route path="n/:type/:id" element={<NotificationLinkPage />} />
					</Route>
					<Route path="*" element={<NotFoundPage />} />
				</Routes>
			</Router>
		</>
	);
}

/** Sends the brand kit's old `/v2/` preview URL to where the page lives now. */
function BrandKitRedirect() {
	const { id, brandId } = useParams();
	return <Navigate to={`/clients/${id}/brands/${brandId}`} replace />;
}
